import React, { useState, useEffect } from "react";
import "../../styles/dash/Profile.css";
import LoadingSpinner from "../utility/LoadingSpinner";
import { updateUserData, changeUserPassword } from "../../services/dashboard/ProfileService";

interface UserDataFromLocalStorage {
  ID: number;
  User: string;
  Email: string;
}

const Profile: React.FC = () => {
  const [newUsername, setNewUsername] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [userUpdateLoading, setUserUpdateLoading] = useState(false);
  const [userUpdateMessage, setUserUpdateMessage] = useState("");
  const [userUpdateError, setUserUpdateError] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeMessage, setPasswordChangeMessage] = useState("");
  const [passwordChangeError, setPasswordChangeError] = useState("");

  useEffect(() => {
    if (userUpdateMessage) {
      const timer = setTimeout(() => {
        setUserUpdateMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [userUpdateMessage]);

  useEffect(() => {
    if (passwordChangeMessage) {
      const timer = setTimeout(() => {
        setPasswordChangeMessage("");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [passwordChangeMessage]);

  useEffect(() => {
    try {
      const userString = localStorage.getItem("user");
      if (userString) {
        const parsedToken = JSON.parse(userString);
        const userData = JSON.parse(parsedToken.userData) as UserDataFromLocalStorage;
        setNewUsername(userData.User || "");
        setNewEmail(userData.Email || "");
      }
    } catch (error) {
      console.error("Error al obtener datos del usuario del localStorage:", error);
    }
  }, []);

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setUserUpdateLoading(true);
    setUserUpdateError("");
    setUserUpdateMessage("");

    try {
      const userString = localStorage.getItem("user");
      if (!userString) throw new Error("No se pudo encontrar la información del usuario.");
      const parsedToken = JSON.parse(userString);
      const userData = JSON.parse(parsedToken.userData) as UserDataFromLocalStorage;
      
      const response = await updateUserData({
        IdUser: userData.ID,
        UserName: newUsername,
        Email: newEmail,
      });

      setUserUpdateMessage(response.message);
      const updatedUserData = { ...userData, User: newUsername, Email: newEmail };
      const updatedToken = { ...parsedToken, userData: JSON.stringify(updatedUserData) };
      localStorage.setItem("user", JSON.stringify(updatedToken));
    } catch (error: any) {
      console.error("Error al actualizar el usuario:", error);
      setUserUpdateError(error.message || "Ocurrió un error al actualizar el usuario.");
    } finally {
      setUserUpdateLoading(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordChangeLoading(true);
    setPasswordChangeMessage("");
    setPasswordChangeError("");

    if (newPassword !== confirmNewPassword) {
      setPasswordChangeError("Las nuevas contraseñas no coinciden.");
      setPasswordChangeLoading(false);
      return;
    }

    try {
      const userString = localStorage.getItem("user");
      if (!userString) throw new Error("No se pudo encontrar la información del usuario.");
      const parsedToken = JSON.parse(userString);
      const userData = JSON.parse(parsedToken.userData) as UserDataFromLocalStorage;

      const response = await changeUserPassword({
        IdUser: userData.ID,
        CurrentPassword: currentPassword,
        NewPassword: newPassword,
      });

      setPasswordChangeMessage(response.message);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (error: any) {
      console.error("Error al cambiar la contraseña:", error);
      setPasswordChangeError(error.message || "Ocurrió un error al cambiar la contraseña.");
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  return (
    <div className="profile-container">
      {userUpdateLoading || passwordChangeLoading ? <LoadingSpinner /> : null}
      <div className="profile-grid">
        <div className="profile-card">
          <h5 className="profile-form-title">Actualizar información</h5>
          <form onSubmit={handleUpdateUser} className="profile-form">
            {userUpdateMessage && (
              <div className="profile-alert profile-success-message">{userUpdateMessage}</div>
            )}
            {userUpdateError && (
              <div className="profile-alert profile-error-message">{userUpdateError}</div>
            )}
            <div className="profile-form-group">
              <label htmlFor="new-username">Nuevo Nombre de Usuario</label>
              <input
                id="new-username"
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
              />
            </div>
            <div className="profile-form-group">
              <label htmlFor="new-email">Nuevo Correo</label>
              <input
                id="new-email"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
            </div>
            <div className="profile-form-group profile-button-group">
              <button type="submit" className="profile-save-button" disabled={userUpdateLoading}>
                {userUpdateLoading ? "Actualizando..." : "Actualizar"}
              </button>
            </div>
          </form>
        </div>
        <div className="profile-card">
          <h5 className="profile-form-title">Cambiar Contraseña</h5>
          <form onSubmit={handleChangePassword} className="profile-form">
            {passwordChangeMessage && (
              <div className="profile-alert profile-success-message">{passwordChangeMessage}</div>
            )}
            {passwordChangeError && (
              <div className="profile-alert profile-error-message">{passwordChangeError}</div>
            )}
            <div className="profile-form-group">
              <label htmlFor="current-password">Contraseña actual</label>
              <input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
            </div>
            <div className="profile-form-group">
              <label htmlFor="new-password">Nueva Contraseña</label>
              <input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div className="profile-form-group">
              <label htmlFor="confirm-new-password">Repetir Contraseña Nueva</label>
              <input
                id="confirm-new-password"
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
              />
            </div>
            <div className="profile-form-group profile-button-group">
              <button type="submit" className="profile-save-button" disabled={passwordChangeLoading}>
                {passwordChangeLoading ? "Cambiando..." : "Cambiar Contraseña"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;